import { FC, useEffect } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useCrudEditContext, useRideTimeChangeCheck } from "@ryde/redux";
import {
  editFormDataInitialSnapshotAF,
  FETCH_RIDE_NEARBY_EXCLUDED_STATUSES,
  IRideDto,
  isShowWasSentApprovalMessageSelector,
  RideCrudModeKeyTuple,
  RideFormFields,
  rideHasNotPriceRateAtom,
  rideHasSignificantTimeChangeAtom,
  rideHasUnsavedStationChangeAtom,
  serverErrorsRideTopInfo,
} from "@ryde/shared";

import { useRideRebuildRouteAppliedInfo } from "./hooks";
import { RideEditTopInfoNearBy } from "./ride-edit-top-info-nearby";
import { RideEditTopInfoContainerStyled } from "./ride.styled";

export const RideEditTopInfoContainer: FC = () => {
  const { t } = useTranslation();
  const rideHasUnsavedStationChange = useRecoilValue(
    rideHasUnsavedStationChangeAtom,
  );
  const { sessionRebuildBannerText } = useRideRebuildRouteAppliedInfo();
  const rideHasSignificantTimeChange = useRecoilValue(
    rideHasSignificantTimeChangeAtom,
  );
  const rideHasNotPriceRate = useRecoilValue(rideHasNotPriceRateAtom);
  const errors = useRecoilValue(serverErrorsRideTopInfo("ride"));

  const watchedIdValue = useWatch({ name: RideFormFields.id });
  const watchedStatusValue = useWatch({ name: RideFormFields.status });
  const watchedDirectionValue = useWatch({ name: RideFormFields.direction });
  const watchedSupplierIdValue = useWatch({ name: RideFormFields.supplierId });
  const watchedVehicleTypeIdValue = useWatch({
    name: RideFormFields.vehicleTypeId,
  });
  const watchedRideStart = useWatch({ name: RideFormFields.rideStart });
  const watchedRideEnd = useWatch({ name: RideFormFields.rideEnd });

  const {
    data: { dto: rideDto, isEdit },
  } = useCrudEditContext<IRideDto>();

  const rideInitialSnapshotDto = useRecoilValue<IRideDto | undefined>(
    editFormDataInitialSnapshotAF(RideCrudModeKeyTuple),
  );

  const isShowWasSentApprovalMessage = useRecoilValue(
    isShowWasSentApprovalMessageSelector,
  );

  const { checkRideTimeChange } = useRideTimeChangeCheck();

  const shouldFetchNearBy =
    watchedIdValue &&
    watchedStatusValue &&
    !FETCH_RIDE_NEARBY_EXCLUDED_STATUSES.includes(watchedStatusValue);

  const isWasSent = Boolean(
    rideDto?.metadata?.wasSent && isShowWasSentApprovalMessage,
  );

  useEffect(() => {
    if (rideInitialSnapshotDto?.id) {
      checkRideTimeChange(rideInitialSnapshotDto, rideDto);
    }
  }, [
    isEdit,
    rideInitialSnapshotDto,
    rideDto,
    watchedRideStart,
    watchedRideEnd,
    checkRideTimeChange,
  ]);

  return (
    <RideEditTopInfoContainerStyled>
      {rideHasUnsavedStationChange && (
        <div className="info_item">
          <p>* {t(`ride.ADDITION_OR_REMOVAL_OF_STATIONS_DETECTED`)}</p>
        </div>
      )}
      {sessionRebuildBannerText && (
        <div className="info_item">
          <p>* {sessionRebuildBannerText}</p>
        </div>
      )}
      {rideHasSignificantTimeChange && (
        <div className="info_item">
          <p>* {t(`ride.RIDE_TIME_CHANGED_ROUTE_WILL_BE_REBUILT`)}</p>
        </div>
      )}
      {!watchedDirectionValue && (
        <div className="info_item">
          <p>* {t(`ride.PLEASE_CHOOSE_DIRECTION_FIRST`)}</p>
        </div>
      )}
      {rideHasNotPriceRate &&
        !watchedSupplierIdValue &&
        !watchedVehicleTypeIdValue && (
          <div className="info_item">
            <p>* {t(`ride.NO_PRICE_RATE_SET_SUPPLIER_AND_VEHICLE_TYPE`)}</p>
          </div>
        )}
      {Boolean(shouldFetchNearBy) && (
        <RideEditTopInfoNearBy rideId={watchedIdValue} />
      )}
      {isWasSent && (
        <div className="info_item">
          <p>* {t(`ride.rideWasSentMessage`)}</p>
        </div>
      )}
      {errors.map((translationKey, i) => (
        <div className="info_item" key={`${i}_${translationKey}`}>
          <p>* {t(translationKey)}</p>
        </div>
      ))}
    </RideEditTopInfoContainerStyled>
  );
};
